import { SlideshowSelectEvent } from '@theme/events';

/**
 * Fandom product gallery (style "fandom" of blocks/_product-media-gallery.liquid).
 *
 * Two elements sit in the overlay over the main image:
 * - <pdp-gallery-counter> keeps "n / total" in step with Horizon's slideshow.
 * - <pdp-wishlist-heart> is a proxy for the ReStock / Notify-Me app's product page wishlist button.
 *
 * Horizon replaces the whole <media-gallery> with the copy it fetched on every variant change, so both elements set
 * themselves up in connectedCallback and keep nothing between instances.
 */

/** The app's product page wishlist button, injected by https://cdn.notify-me.io/sdk.js. */
const APP_BUTTON_SELECTOR = 'button.nm-PRODUCT_PAGE-wishlist-button';

/**
 * Shows the 1-based position of the visible slide. Liquid renders the `pdp.image_count` string into `data-template`
 * with "%i" where the position goes, and the total into `data-total`.
 */
class PdpGalleryCounter extends HTMLElement {
  /** @type {HTMLElement | null} */
  #slideshow = null;

  connectedCallback() {
    this.#slideshow = this.closest('slideshow-component');
    this.#slideshow?.addEventListener(SlideshowSelectEvent.eventName, this.#handleSelect);

    // The shopper may have swiped before this module loaded.
    const current = /** @type {{ current?: unknown } | null} */ (this.#slideshow)?.current;
    if (typeof current === 'number') this.#render(current);
  }

  disconnectedCallback() {
    this.#slideshow?.removeEventListener(SlideshowSelectEvent.eventName, this.#handleSelect);
    this.#slideshow = null;
  }

  /** @param {Event} event */
  #handleSelect = (event) => {
    if (event.target !== this.#slideshow) return;
    const index = /** @type {SlideshowSelectEvent} */ (event).detail?.index;
    if (typeof index === 'number') this.#render(index);
  };

  /** @param {number} index - 0-based slide index */
  #render(index) {
    const output = this.querySelector('.pdp-gallery__count');
    const total = Number(this.dataset.total) || 0;
    if (!output || total < 1) return;

    const position = Math.min(Math.max(index + 1, 1), total);
    const template = this.dataset.template || `%i / ${total}`;
    output.textContent = template.replace('%i', String(position));
  }
}

/**
 * Reads the app button's saved state. The app keeps the same classes and attributes either way and only re-renders
 * the button content: its icon path gets an inline `fill` other than "none", and the label becomes the "remove from
 * wishlist" text from `window._ReStockConfig.wishlist.ui_contents`. The label is the fallback when the app is set to
 * show no icon.
 * @param {HTMLElement} button
 * @returns {boolean}
 */
function isSaved(button) {
  const path = button.querySelector('svg path');
  if (path instanceof SVGElement) {
    const fill = path.style.fill || path.getAttribute('fill') || '';
    if (fill) return fill !== 'none';
  }

  const label = button.textContent?.trim() ?? '';
  /** @type {Record<string, { remove_from_wishlist_button_label?: string }>} */
  const contents = /** @type {any} */ (window)._ReStockConfig?.wishlist?.ui_contents ?? {};
  return (
    label !== '' &&
    Object.values(contents).some((content) => content?.remove_from_wishlist_button_label?.trim() === label)
  );
}

/**
 * The heart over the main image. A click clicks the app's own wishlist button, so the app keeps handling guests,
 * login and its per-variant list; the heart mirrors that button through a MutationObserver. While the app button is
 * missing (not injected yet, or the app is uninstalled) the heart stays hidden.
 */
class PdpWishlistHeart extends HTMLElement {
  /** @type {ParentNode} */
  #root = document;
  /** @type {HTMLButtonElement | null} */
  #button = null;
  /** @type {HTMLButtonElement | null} */
  #appButton = null;
  /** @type {MutationObserver | null} */
  #rootObserver = null;
  /** @type {MutationObserver | null} */
  #appObserver = null;

  connectedCallback() {
    // Only the Fandom product page (.pdp) has the heart proxy; elsewhere the app's button is not ours to follow.
    if (!this.closest('.pdp')) return;
    this.#root = this.closest('.shopify-section') ?? document.body;
    this.#button = this.querySelector('button.pdp-gallery__heart');
    this.#button?.addEventListener('click', this.#handleClick);

    // The app injects its button after load, next to the buy buttons in this same section, and may re-create it.
    this.#rootObserver = new MutationObserver(this.#bind);
    this.#rootObserver.observe(this.#root, { childList: true, subtree: true });
    this.#bind();
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#handleClick);
    this.#rootObserver?.disconnect();
    this.#appObserver?.disconnect();
    this.#rootObserver = null;
    this.#appObserver = null;
    this.#appButton = null;
    this.#button = null;
  }

  /** Finds the app button and follows it; does nothing while it is the same node. */
  #bind = () => {
    const found = this.#root.querySelector(APP_BUTTON_SELECTOR);
    const appButton = found instanceof HTMLButtonElement ? found : null;
    if (appButton === this.#appButton) return;

    this.#appObserver?.disconnect();
    this.#appObserver = null;
    this.#appButton = appButton;
    this.hidden = !appButton;
    if (!appButton) return;

    this.#appObserver = new MutationObserver(this.#sync);
    this.#appObserver.observe(appButton, { subtree: true, childList: true, characterData: true, attributes: true });
    this.#sync();
  };

  /** Copies the app's saved state onto `data-saved` and the heart's accessible name. */
  #sync = () => {
    if (!this.#appButton) return;
    const saved = isSaved(this.#appButton);
    this.dataset.saved = String(saved);
    const label = saved ? this.dataset.labelSaved : this.dataset.label;
    if (label) this.#button?.setAttribute('aria-label', label);
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    event.preventDefault();
    this.#bind();
    this.#appButton?.click();
  };
}

if (!customElements.get('pdp-gallery-counter')) {
  customElements.define('pdp-gallery-counter', PdpGalleryCounter);
}

if (!customElements.get('pdp-wishlist-heart')) {
  customElements.define('pdp-wishlist-heart', PdpWishlistHeart);
}
